import { ref } from "vue";
import { useLanguageChanger } from "./useLanguageChanger";
import { LogoutHookParams } from "@/global";

function getNativeAppContext() {
  return globalThis.DulconnexNativeApp;
}

/**
 * This composable can be used for anything about the an optionally wrapping native App.
 */
export function useAppWrapper() {
  /** Check if we are currently mounted inside of a ReactNative webview */
  const isReactNative = ref(globalThis?.ReactNativeWebView !== undefined);

  /** Check if the App has native download capabilities */
  const supportsNativeDownload = ref(getNativeAppContext()?.capabilities?.downloadFile === true);

  /** Check if the App has native change language capabilities */
  const supportsNativeLanguageChange = ref(getNativeAppContext()?.capabilities?.setLanguage === true);

  async function beforeLogoutHook(params: LogoutHookParams) {
    await globalThis.DulconnexNativeApp?.beforeLogout?.(params);
  }

  /**
   * Use the ReactNativeWebView postMessage function to send a message to the App Wrapper.
   *
   * Fails silently if not inside of ReactNativeWebView.
   */
  function postMessageToApp(message: string) {
    globalThis.ReactNativeWebView?.postMessage(message);
  }

  /**
   * Initialize the DulconnexNativeBridge to enable communication between native and web.
   *
   * This Fails silently, if DX is not inside of a ReactNative Webview.
   *
   * @todo use "message" event to listen to the App, instead of polluting global variable
   * @todo setup globally usable messaging system
   */
  function initAppBridge() {
    if (!isReactNative.value) return;

    const { changeLanguage } = useLanguageChanger();

    globalThis.DulconnexNativeBridge = {
      async setLanguage(language: string) {
        await changeLanguage(language.toLowerCase());
      },
    };

    postMessageToApp(
      JSON.stringify({
        type: "dulconnexBridgeReady",
      })
    );
  }

  return {
    postMessageToApp,
    initAppBridge,
    isReactNative,
    supportsNativeDownload,
    supportsNativeLanguageChange,
    beforeLogoutHook,
  };
}